import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Shield, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth, homeForRoles } from '@/context/AuthContext.jsx';
import LanguageSwitcher from '@/components/common/LanguageSwitcher.jsx';

export default function Login() {
  const { t } = useTranslation();
  const { login, isAuthenticated, homeFor, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const search = new URLSearchParams(location.search);
  const next   = search.get('next');
  const reason = search.get('reason');

  const [form,    setForm]    = useState({ email: '', password: '' });
  const [errors,  setErrors]  = useState({});
  const [submit,  setSubmit]  = useState(false);
  const [serverErr, setServerErr] = useState(reason === 'session_expired'
    ? t('auth.login.sessionExpired')
    : null);

  // If already authenticated (after the BE validates the cached token
  // — see AuthContext useEffect), send to the right home. We deliberately
  // wait for authLoading to settle so a stale-cache user doesn't get
  // redirected before /auth/me has a chance to 401 and clear the
  // session.
  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) {
      navigate(next || homeFor() || '/', { replace: true });
    }
  }, [authLoading, isAuthenticated, homeFor, navigate, next]);

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
    if (serverErr) setServerErr(null);
  }

  function validate() {
    const e = {};
    if (!form.email.trim())  e.email = t('auth.login.errors.emailRequired');
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = t('auth.login.errors.emailInvalid');
    if (!form.password)      e.password = t('auth.login.errors.passwordRequired');
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev) {
    ev.preventDefault();
    if (!validate()) return;
    setSubmit(true);
    setServerErr(null);
    try {
      const me = await login(form.email.trim(), form.password);
      const dest = next || pickHomeForUser(me) || '/';
      navigate(dest, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const data   = err?.response?.data || {};
      const msg    = data.message;

      // Email not yet verified — the BE issued a fresh code; route
      // the user to the OTP entry page with dev_otp in router state.
      if (status === 403 && data.requires_verification) {
        navigate('/verify-otp', {
          replace: true,
          state: {
            email:  data.email || form.email.trim(),
            devOtp: data.dev_otp || null,
            from:   'login',
          },
        });
        return;
      }

      if (status === 422 || status === 401) {
        setServerErr(msg || t('auth.login.errors.invalid'));
      } else if (status === 423) {
        setServerErr(t('auth.login.errors.locked'));
      } else if (!err?.response) {
        setServerErr(t('auth.login.errors.network'));
      } else {
        setServerErr(msg || t('auth.login.errors.generic'));
      }
    } finally {
      setSubmit(false);
    }
  }

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left: brand panel */}
      <aside className="relative hidden bg-slate-950 lg:flex lg:flex-col lg:p-12 lg:text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              'radial-gradient(900px circle at 0% 0%, rgba(56,189,248,0.18), transparent 45%), radial-gradient(800px circle at 100% 100%, rgba(14,165,233,0.18), transparent 45%)',
          }}
          aria-hidden
        />
        <div className="relative flex flex-1 flex-col">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-white/10">
              <Shield className="h-6 w-6" strokeWidth={2.4} />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-wide">{t('brand.name')}</div>
              <div className="text-[10px] uppercase tracking-[0.14em] opacity-70">{t('brand.full')}</div>
            </div>
          </Link>

          <div className="mt-auto max-w-md">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-sky-300">{t('auth.login.operationalSignIn')}</div>
            <h2 className="mt-3 text-3xl font-semibold leading-tight">
              {t('auth.login.heroHeadline')}
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-300">
              {t('auth.login.heroSubtext')}
            </p>
          </div>
        </div>
      </aside>

      {/* Right: form panel */}
      <main className="relative flex items-center justify-center px-6 py-12 lg:px-12">
        <div className="absolute right-4 top-4 lg:right-8 lg:top-8">
          <LanguageSwitcher variant="light" />
        </div>
        <div className="w-full max-w-sm">
          <div className="mb-2 flex items-center gap-2 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-900 text-white">
              <Shield className="h-5 w-5" strokeWidth={2.4} />
            </div>
            <div className="text-sm font-bold tracking-wide text-slate-900">{t('brand.name')}</div>
          </div>

          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{t('auth.login.title')}</h1>
          <p className="mt-1.5 text-sm text-slate-600">
            {t('auth.login.subtitle')}
          </p>

          {serverErr && (
            <div role="alert" className="mt-6 flex gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>{serverErr}</div>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-6 space-y-5" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-800">{t('auth.login.emailLabel')}</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={e => update('email', e.target.value)}
                className="input mt-1"
                disabled={submit}
              />
              {errors.email && <div className="mt-1 text-xs text-rose-600">{errors.email}</div>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-800">{t('auth.login.passwordLabel')}</label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                value={form.password}
                onChange={e => update('password', e.target.value)}
                className="input mt-1"
                disabled={submit}
              />
              {errors.password && <div className="mt-1 text-xs text-rose-600">{errors.password}</div>}
            </div>

            <button type="submit" className="btn-primary w-full" disabled={submit}>
              {submit ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('auth.login.submitting')}</> : t('auth.login.submit')}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-between text-xs">
            <Link to="/register" className="font-medium text-sky-700 hover:text-sky-800">
              Create a citizen account
            </Link>
            <Link to="/forgot-password" className="font-medium text-sky-700 hover:text-sky-800">
              Forgot password?
            </Link>
          </div>

          <div className="mt-8 text-xs text-slate-500">
            {t('auth.login.needAccess')}{' '}
            <Link to="/for-agencies" className="font-medium text-sky-700 hover:text-sky-800">
              {t('auth.login.requestAgencyAccess')}
            </Link>{' · '}
            <Link to="/for-partners" className="font-medium text-sky-700 hover:text-sky-800">
              {t('auth.login.partnershipTiers')}
            </Link>
          </div>

          <div className="mt-6 border-t border-slate-200 pt-6 text-xs text-slate-500">
            {t('auth.login.troubleSigningIn')}
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Post-login destination.
 *
 * Delegates to the canonical homeForRoles(). This used to keep a private copy
 * of the role→home map, which had already drifted: it sent judges,
 * magistrates and prosecutors to /hub while the canonical map — and the
 * already-authenticated redirect above, which calls homeFor() — sent them to
 * /judicial. Same page, two destinations, depending only on whether you had
 * just signed in or were returning.
 */
function pickHomeForUser(me) {
  return homeForRoles(me?.roles ?? []);
}
